import { useState, useRef, useEffect, ImgHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

interface OptimizedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'onLoad' | 'onError'> {
  src: string;
  alt: string;
  priority?: boolean;
  className?: string;
  containerClassName?: string;
  blurDataURL?: string;
  onLoaded?: () => void;
}

/**
 * Generates a tiny blur placeholder URL from Unsplash images
 * Uses 16px width for minimal data transfer
 */
const getBlurPlaceholder = (src: string): string | null => {
  if (!src) return null;
  
  // For Unsplash images, create a tiny version for blur
  if (src.includes('unsplash.com')) {
    const baseUrl = src.split('?')[0];
    return `${baseUrl}?w=16&q=10&blur=20`;
  }
  
  // For Supabase storage, we can't transform, so return null
  return null;
};

/**
 * OptimizedImage component with blur placeholder and progressive loading
 * - Shows a blurred low-res version while the full image loads
 * - Smooth transition from blur to sharp
 * - Supports priority loading for above-the-fold images
 */
const OptimizedImage = ({
  src,
  alt,
  priority = false,
  className,
  containerClassName,
  blurDataURL,
  onLoaded,
  ...props
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  
  const placeholder = blurDataURL || getBlurPlaceholder(src);

  useEffect(() => {
    // Reset states when src changes
    setIsLoaded(false);
    setHasError(false);
    
    // Check if image is already cached
    if (imgRef.current?.complete && imgRef.current?.naturalHeight > 0) {
      setIsLoaded(true);
    }
  }, [src]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoaded?.();
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  return (
    <div className={cn("relative overflow-hidden", containerClassName)}>
      {/* Blur placeholder layer */}
      {placeholder && !isLoaded && !hasError && (
        <img
          src={placeholder}
          alt=""
          aria-hidden="true"
          className={cn(
            "absolute inset-0 w-full h-full object-cover",
            "scale-110 blur-lg",
            className
          )}
        />
      )}
      
      {/* Shimmer placeholder when no blur available */}
      {!placeholder && !isLoaded && !hasError && (
        <div 
          className={cn(
            "absolute inset-0 bg-gradient-to-r from-muted via-muted/50 to-muted",
            "animate-pulse"
          )}
        />
      )}
      
      {/* Main image */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        loading={priority ? "eager" : "lazy"}
        decoding={priority ? "sync" : "async"}
        fetchPriority={priority ? "high" : undefined}
        className={cn(
          "w-full h-full object-cover",
          "transition-opacity duration-500 ease-out",
          isLoaded ? "opacity-100" : "opacity-0",
          className
        )}
        {...props}
      />
    </div>
  );
};

export default OptimizedImage;
